import {
  Box,
  CheckboxField,
  DatePickerField,
  DateRangePickerField,
  ErrorMessage,
  Flex,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  hint?: string;
  isStartOnly?: boolean;
};

export const PeriodAndStartEndCheckField: FC<Props> = ({
  hint,
  isStartOnly,
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors, isSubmitted },
    watch,
    setValue,
    resetField,
    trigger,
  } = useProjectFormContext();
  const [fiscalYear, period] = watch(['data.fiscalYear', 'data.period']);

  const handleChangeCheckbox = useCallback(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [isSubmitted, trigger]);

  return (
    <Box>
      <Flex gap={2} align="flex-end">
        {isStartOnly ? (
          <DatePickerField
            name="data.period"
            label={t('start-fiscal-year-of-project')}
            hint={hint}
            control={control}
            errors={errors}
            picker="year"
            disabled={!fiscalYear}
            wrapperStyle={{ marginBottom: 0 }}
            onChange={(period) => {
              if (period) {
                resetField('data.isUnknownStart', {
                  defaultValue: false,
                });
              }
              // NOTE: react-hook-form 7.76 で setValue が値変化なしのとき
              // 内部の set をスキップする最適化が入ったため、
              // `data.period.1` を undefined で書く no-op に依存できなくなった。
              // 配列全体を一度に書き、zod tuple の長さ2要求を満たす。
              setValue('data.period', [period ?? undefined, undefined], {
                shouldDirty: true,
              });
              handleChangeCheckbox();
            }}
            required
            hiddenErrorMessage
          />
        ) : (
          <DateRangePickerField
            name="data.period"
            label={t('period')}
            hint={hint}
            control={control}
            errors={errors}
            picker="year"
            allowEmpty={[true, true]}
            disabled={!fiscalYear}
            shouldCoexistOnChange
            wrapperStyle={{ marginBottom: 0 }}
            hiddenErrorMessage
            onChange={(period) => {
              if (period?.[0]) {
                resetField('data.isUnknownStart', {
                  defaultValue: false,
                });
              }
              if (period?.[1]) {
                resetField('data.isNoScheduledEnd', {
                  defaultValue: false,
                });
              }
            }}
            required
          />
        )}

        <CheckboxField
          name="data.isUnknownStart"
          text={t('unknown-start')}
          control={control}
          errors={errors}
          disabled={!!period?.[0]}
          wrapperStyle={{ marginBottom: 0 }}
          shouldCoexistOnChange
          onChange={handleChangeCheckbox}
        />
        {!isStartOnly && (
          <CheckboxField
            name="data.isNoScheduledEnd"
            text={t('no-scheduled-end')}
            control={control}
            errors={errors}
            disabled={!!period?.[1]}
            wrapperStyle={{ marginBottom: 0 }}
            shouldCoexistOnChange
            onChange={handleChangeCheckbox}
          />
        )}
      </Flex>

      <ErrorMessage errors={errors} name="data.period" />
    </Box>
  );
};
